import { Page } from 'libraries/Page';
import { Line } from 'libraries/Line';
import { FontsService } from 'services/fonts/fonts.service';
import { SignInfo } from 'domain-models/SignInfo';

const Tags = {
	NewPage: '[np]',
	NewLine: '[nl]'
};

const predefinedMessages = [
	{
		resource: 'left_arrow',
		multistring:
			'[sc0][hc1C][hc8][hcB][hc1F][nl][hc7][sc5][hc9][sc0][hcC][hcC][hcC][hcC][hcC][hcC][nl][hc1E][hcA][hcD][hc1D]'
	}
];

export default class MultistringUtils {
	/**
	 * Parse the given multistring
	 * @param multistring Multistring to be parsed
	 * @returns {Page[]} Array of Pages
	 */
	static parse(signInfo: SignInfo, fontsService: FontsService) {
		let pages: Page[] = [];
		let multistring = signInfo.CurrentMessage.replace(/\[nl\d{1,2}\]/g, '[nl]');
		let pageStrings: string[] = multistring.split(Tags.NewPage);

		pageStrings.forEach((pageString, pageNumber) => {
			let page = this.parsePage(pageString, pageNumber, fontsService, signInfo);
			pages.push(page);
		});

		return pages;
	}

	static construct(pages: Array<Page>) {
		let multistring = '';

		let preparedPages = pages
			.map((page, pageNumber) => {
				return this.preparePage(page, pageNumber);
			})
			.filter((page) => page !== '');

		if (preparedPages.length === 1) {
			preparedPages.forEach((page, index, arr) => {
				arr[index] = this.stripFlashTags(page);
			});
		}

		multistring += preparedPages.join(Tags.NewPage);

		return multistring;
	}

	public static checkPredefined(multistring: string) {
		let message = predefinedMessages.find((m) => {
			let result = multistring.toLowerCase().includes(m.multistring.toLowerCase());
			return result;
		});
		if (message) {
			console.log('Found arrow***********');
			return message.resource;
		} else {
			return;
		}
	}

	private static preparePage(page: Page, pageNumber: number) {
		let preparedPage = '';

		let preparedLines = page.lines
			.map((line, lineNumber) => {
				return this.prepareLine(line, lineNumber);
			})
			.filter((line) => line !== ''); // Remove empty lines (so they won't get a [nl] put between them)

		let tags = '';
		tags += this.constructPageFlash(page.flashOn, page.flashOff);

		if (page.font && page.lines.filter((l) => l.font && l.font.Id !== page.font.Id).length === 0) {
			tags += this.constructFont(page.font.SignFontIndex);
			preparedLines.forEach((line, index, arr) => {
				// Remove individual line fonts since they are the same as the page font
				arr[index] = this.stripFonts(line);
			});
		}

		tags += this.constructPageJustification(page.justification);

		if (preparedLines.length > 0) {
			preparedPage += tags;
			preparedPage += preparedLines.join(Tags.NewLine);
		}

		return preparedPage;
	}

	private static prepareLine(line: Line, lineNumber: number) {
		let preparedLine = '';

		if (line.text || line.justifications[3] || line.justifications[2] || line.justifications[4]) {
			preparedLine += this.constructFont(line.font.SignFontIndex);
			preparedLine += this.constructLineJustifications(line.justifications);
			// preparedLine += line.text;
		}

		return preparedLine;
	}

	private static constructFont(font: number) {
		if (font == undefined) return '';
		return `[fo${font}]`;
	}

	private static constructPageFlash(flashOn: number, flashOff: number) {
		if (flashOn == undefined || flashOff == undefined) return '';
		return `[pt${flashOn * 10}o${flashOff == 0 ? '00' : flashOff * 10}]`;
	}

	private static constructPageJustification(justification: number) {
		// Do page justifications affect other pages if no justification is specified for a later page?
		if (justification == undefined || justification === 3) return '';
		return `[jp${justification}]`;
	}

	private static constructLineJustifications(justifications: object) {
		let justifiedText: string = '';

		// We don't append [jl3] by default because the sign will automatically interpret it that way
		for (let i = 0; i <= 4; i++) {
			if (justifications[i]) {
				if (i === 3 && !justifications[2] && !justifications[4]) {
					justifiedText += justifications[i].toUpperCase();
				} else {
					justifiedText += `[jl${i}]${justifications[i].toUpperCase()}`;
				}
			}
		}

		return justifiedText;
	}

	/**
	 * Parse a multistring page
	 * @param pageString Page section of multistring as a string
	 * @param pageNumber Page number of the passed page section
	 * @returns {Page} A Page object based on the information parsed from the passed pageString
	 */
	private static parsePage(
		pageString: string,
		pageNumber: number,
		fontsService: FontsService,
		signInfo: SignInfo
	): Page {
		// Important Note: this works because the param name (pageNumber) is the same as the variable name
		let page: Page = new Page(signInfo.NumberOfLines, pageNumber);
		page.lines = [];

		// Parse Font
		let numFontTags = this.numFontTags(pageString); // This will be important later
		let pageFontId = this.getFont(pageString);
		if (pageFontId !== -1) {
			page.setFont(pageFontId, fontsService);
		}

		// Parse time on/off
		page.flashOn = this.getPageTimeOn(pageString);
		page.flashOff = this.getPageTimeOff(pageString);

		// Parse justifications
		let pageJustification = this.getPageJustification(pageString);
		page.setJustification(pageJustification);

		// Handle predefined messages (arrows, etc)
		let resource = this.checkPredefined(pageString);
		if (resource) {
			page.imageResource = resource;
		} else {
			let lineStrings: string[] = pageString.split(Tags.NewLine);
			lineStrings.forEach((lineString, lineNumber) => {
				let line: Line = this.parseLine(
					lineString,
					lineNumber,
					fontsService,
					numFontTags === 1 ? pageFontId : null
				);
				page.lines.push(line);
			});
		}

		return page;
	}

	/**
	 * Parse a multistring line
	 * @param lineString Line section of multistring as a string
	 * @param lineNumber Line number of the passed line section
	 * @returns {Line} A Line object based on the information parsed from the passed lineString
	 */
	private static parseLine(
		lineString: string,
		lineNumber: number,
		fontsService: FontsService,
		pageFont = null
	): Line {
		let line: Line = new Line();

		lineString = this.stripPageTags(lineString);

		// Parse Font
		let lineFontId;
		if (!pageFont) {
			lineFontId = this.getFont(lineString);
			if (lineFontId !== -1) line.setFont(lineFontId, fontsService);
		} else {
			// The page sent a font override to the line parser
			line.setFont(pageFont, fontsService);
		}
		lineString = this.stripFonts(lineString);

		// Save the color foreground sections and remove those tags
		line.foregroundColors = this.getColorForegroundSections(lineString);
		lineString = this.stripColorForegroundTags(lineString);

		// Replace hexadecimal character with ASCII character representation
		lineString = this.convertHexadecimalTags(lineString);

		// Remove [scx] tags
		// TODO: interpret and use these tag values
		lineString = this.stripCharSpacingTags(lineString);

		// Parse justifications
		line.justifications = this.getLineJustifications(lineString);

		line.text = this.stripNtcipTags(lineString);

		return line;
	}

	// TODO these need to be tested
	// Preferably automatically (through a spec.ts or something)
	private static getFont(stringSection: string) {
		let result = -1;
		const regex = /(?:\[fo(\d)\])/;

		let match = regex.exec(stringSection);

		if (match) {
			result = Number(match[1]);
		}

		return result;
	}

	private static getPageTimeOn(stringSection: string) {
		let result = 20;
		const regex = /(?:\[pt(\d+)o\d+\])/;

		let match = regex.exec(stringSection);

		if (match) {
			result = Number(match[1]);
		}

		return result;
	}

	private static getPageTimeOff(stringSection: string) {
		let result = 0;
		const regex = /(?:\[pt\d+o(\d+)\])/;

		let match = regex.exec(stringSection);

		if (match) {
			result = Number(match[1]);
		}

		return result;
	}

	private static getPageJustification(stringSection: string) {
		let result = 3;
		const regex = /(?:\[jp(\d)\])/;

		let match = regex.exec(stringSection);

		if (match) {
			result = Number(match[1]);
		}

		return result;
	}

	private static getLineJustifications(stringSection: string) {
		let justifications = {};
		const regex = /(?:\[jl(\d)\]([^\[]*))/g;

		let match;

		let hasExplicitJustification = false;
		while ((match = regex.exec(stringSection))) {
			hasExplicitJustification = true;
			justifications[match[1]] = match[2];
		}

		// Set the entire text to centered if no justifications are specified
		if (!hasExplicitJustification) {
			justifications[3] = stringSection;
		}

		return justifications;
	}

	/**
	 * From ATIS library
	 *
	 * @param stringSection
	 * @returns
	 */
	public static getColorForegroundSections(
		stringSection: string
	): { color: string; text: string }[] {
		let sections = [];

		// const regex = /(?:\[cf(\d{0,1})\]([^\[]*))/g;
		const regex = /(?:\[cf(\d{0,1})\]([^\[]*))/g;

		let match;
		while ((match = regex.exec(stringSection))) {
			console.log(match);
			let color = match[1];
			let text = match[2];

			if (color == '') {
				color = undefined;
			}

			sections.push({ color, text });
		}

		if (sections.length == 0) {
			sections.push({ color: undefined, text: stringSection });
		}

		// let segments = stringSection.split(/(?:\[cf\d{0,1}\])/g);
		// let index = 0;

		// console.log(segments);
		// console.log(sections);

		// if (segments.length === sections.length) {
		// 	// Good to go, no segments of text prior to cf tags
		// } else if (segments.length === sections.length + 1) {
		// 	sections.unshift({ color: undefined, text: '' });
		// } else {
		// 	throw new Error('Something weird is going on');
		// }

		// sections.forEach((section, index) => {
		// 	section.text = segments[index];
		// });

		return sections;
	}

	private static stripNtcipTags(stringSection: string) {
		let result: string;
		result = stringSection
			.replace(/\[pt\d+o\d+\]/, '')
			.replace(/\[jp\d{1}\]/, '')
			.replace(/\[jl\d{0,1}\]/g, '')
			.replace(/\[fo\d{1,2}\]/, '')
			.replace(/\[cf\d{1,2}\]/, '');

		return result;
	}

	private static stripFonts(stringSection: string) {
		return stringSection.replace(/\[fo\d{1,2}\]/, '');
	}

	private static stripPageTags(stringSection: string) {
		return stringSection.replace(/\[pt\d+o\d+\]/, '').replace(/\[jp\d{1}\]/, '');
	}

	private static stripFlashTags(stringSection: string) {
		return stringSection.replace(/\[pt\d+o\d+\]/, '');
	}

	private static numFontTags(stringSection: string) {
		let result = 0;
		const regex = /(?:\[fo(\d)\])/;

		let matches = regex.exec(stringSection);

		if (matches) {
			result = matches.length / 2; // Two elements for each actual match
		}

		return result;
	}

	/**
	 * NTCIP 6.4.3 - Color Foreground
	 *
	 * Returns the numeric 0-9 value from the tag, or undefined for default
	 *
	 * Corresponds to a value from the `colorClassic` color list
	 *
	 * @param tag string of the format `[cfx]`
	 */
	public static interpretColorForegroundTag(tag: string): Number {
		let result;

		const regex = /(?:\[cf(\d{0,1})\])/;

		let match = regex.exec(tag);

		if (match) {
			if (match[1] !== '') {
				result = Number(match[1]);
			}
		} else {
			throw new Error('No [cfx] tag found.');
		}

		return result;
	}

	/**
	 * NTCIP 6.4.3 - Color Foreground
	 *
	 * Removes all `[cf]`/`[cfx]` tags from a string
	 *
	 * @param stringSection The string from which to remove tags
	 */
	public static stripColorForegroundTags(stringSection: string) {
		return stringSection.replace(/\[cf(\d{0,1})\]/g, '');
	}

	/**
	 * NTCIP 6.4.9 - Hexadecimal Character
	 *
	 * Returns the hexadecimal value from the tag
	 *
	 * @param tag string of the format `[hcx]`
	 */
	public static interpretHexadecimalCharacterTag(tag: string) {
		let result;

		const regex = /(?:\[hc(\w{1,4})\])/;

		let match = regex.exec(tag);

		if (match) {
			result = match[1];
		}

		return result;
	}

	/**
	 * NTCIP 6.4.9 - Hexadecimal Character
	 *
	 * Converts all hexadecimal tags to their ASCII equivalents
	 *
	 * @param text The string on which to operate
	 */
	public static convertHexadecimalTags(text: string) {
		let result;

		const regex = /\[hc\w{1,4}\]/g;

		result = text.replace(regex, (match) => {
			const hex = this.interpretHexadecimalCharacterTag(match);
			return String.fromCharCode(parseInt(hex, 16));
		});

		return result;
	}

	/**
	 * NTCIP 6.4.9 - Hexadecimal Character
	 *
	 * Removes all `[hcx]` tags from a string
	 *
	 * @param stringSection The string from which to remove tags
	 */
	public static stripHexCharTags(stringSection: string) {
		return stringSection.replace(/\[hc(\w+)\]/g, '');
	}

	/**
	 * NTCIP 6.4.14 - New Line
	 *
	 * Returns the (optional) value from the tag, or `undefined` if no value
	 *
	 * Corresponds to the spacing in pixes between the lines
	 *
	 * Defaults to the font's line spacing if not defined (`[nl]`)
	 *
	 * @param tag string of the format `[nlx]`
	 */
	public static interpretNewLineTag(tag: string) {
		let result;

		const regex = /(?:\[nl(\d{0,1})\])/;

		let match = regex.exec(tag);

		console.log(match);

		if (match) {
			if (match[1] !== '') {
				result = Number(match[1]);
			}
		} else {
			throw new Error('No [nlx] tag found.');
		}

		return result;
	}

	/**
	 * NTCIP 6.4.14 - New Line
	 *
	 * Removes all `[nl]`/`[nlx]` tags from a string
	 *
	 * @param stringSection The string from which to remove tags
	 */
	public static stripNewLineTags(stringSection: string) {
		return stringSection.replace(/\[nl(\d{0,1})\]/g, '');
	}

	/**
	 * NTCIP 6.4.17 - Spacing - Character
	 *
	 * Returns the numeric 0-9 value from the tag
	 *
	 * @param tag string of the format `[scx]`
	 */
	public static interpretCharSpacingTag(tag: string): Number {
		let result;

		const regex = /(?:\[sc(\d)\])/;

		let match = regex.exec(tag);

		if (match) {
			result = Number(match[1]);
		}

		return result;
	}

	/**
	 * NTCIP 6.4.17 - Spacing - Character
	 *
	 * Removes all `[scx]` tags from a string
	 *
	 * @param stringSection The string from which to remove tags
	 */
	public static stripCharSpacingTags(stringSection: string) {
		return stringSection.replace(/\[sc(\d)\]/g, '');
	}

	/**
	 * ! Make sure you parse out needed values from specific tags
	 * ! before calling this!
	 */
	public static stripAllTags(stringSection: string) {
		return stringSection.replace(/\[[\/\w]*\]/g, '');
	}
}



// WEBPACK FOOTER //
// /Users/baselkilani/work/ODOT/projects/dmscontrol/DmsControl/src/libraries/MultistringUtils.ts